export type ConnectionStatus = 'good' | 'degraded' | 'critical';

// Thresholds calibrated for a fiber / 5G fleet: they flag deviation from the
// expected infrastructure, not just functional failure.
export const NETWORK_THRESHOLDS = {
  latencyMs: { degraded: 100, critical: 200 },
  jitterMs: { degraded: 50, critical: 90 },
  lossPercent: { degraded: 1, critical: 3 },
  downloadMbps: { degraded: 25, critical: 5 }, // below threshold → degraded/critical
  uploadMbps: { degraded: 10, critical: 2 },
} as const;

export const NETWORK_STATUS_RANK: Record<ConnectionStatus, number> = {
  good: 0,
  degraded: 1,
  critical: 2,
};

export const NETWORK_STATUS_STYLES: Record<ConnectionStatus, string> = {
  good: 'bg-green-500/15 text-green-700 dark:text-green-400',
  degraded: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  critical: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

export const NETWORK_STATUS_LABELS: Record<ConnectionStatus, string> = {
  good: 'Bonne',
  degraded: 'Dégradée',
  critical: 'Critique',
};

export function rateUpperThreshold(
  value: number | null | undefined,
  t: { degraded: number; critical: number },
): ConnectionStatus | null {
  if (value == null || Number.isNaN(value)) return null;
  if (value > t.critical) return 'critical';
  if (value > t.degraded) return 'degraded';
  return 'good';
}

export function rateLowerThreshold(
  value: number | null | undefined,
  t: { degraded: number; critical: number },
): ConnectionStatus | null {
  if (value == null || Number.isNaN(value)) return null;
  if (value < t.critical) return 'critical';
  if (value < t.degraded) return 'degraded';
  return 'good';
}

export function worstNetworkStatus(...statuses: (ConnectionStatus | null | undefined)[]): ConnectionStatus {
  let worst: ConnectionStatus = 'good';
  for (const s of statuses) {
    if (s && NETWORK_STATUS_RANK[s] > NETWORK_STATUS_RANK[worst]) {
      worst = s;
    }
  }
  return worst;
}

export interface NetworkMetricsInput {
  latencyMs?: number | null;
  jitterMs?: number | null;
  lossPercent?: number | null;
  downloadMbps?: number | null;
  uploadMbps?: number | null;
}

/**
 * Computes the overall network quality status directly from the raw metrics
 * using the shared NETWORK_THRESHOLDS.
 */
export function computeMetricsStatus(metrics?: NetworkMetricsInput | null): ConnectionStatus | null {
  if (!metrics) return null;
  const { latencyMs, jitterMs, lossPercent, downloadMbps, uploadMbps } = metrics;
  const hasAnyMetric =
    latencyMs != null ||
    jitterMs != null ||
    lossPercent != null ||
    downloadMbps != null ||
    uploadMbps != null;

  if (!hasAnyMetric) return null;

  return worstNetworkStatus(
    rateUpperThreshold(latencyMs, NETWORK_THRESHOLDS.latencyMs),
    rateUpperThreshold(jitterMs, NETWORK_THRESHOLDS.jitterMs),
    rateUpperThreshold(lossPercent, NETWORK_THRESHOLDS.lossPercent),
    rateLowerThreshold(downloadMbps, NETWORK_THRESHOLDS.downloadMbps),
    rateLowerThreshold(uploadMbps, NETWORK_THRESHOLDS.uploadMbps),
  );
}
